"use client";

import {
  MoreVertical,
  ArrowRight,
  ArrowDownToLine,
  ArrowUpFromLine,
  Plus,
  ArrowUp,
} from "lucide-react";
import { useUserTransactionHistory } from "../hooks/query/queries";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { useEffect, useState } from "react";
import { Skeleton } from "./ui/skeleton";
import TransactionHistoryAll from "./TransactionHistoryAll";
import TransactionHistorySingle from "./TransactionHistorySingle";
import arbIcon from "../assets/icons/arb-payout.svg";
import approvedIcon from "../assets/icons/approved-icon.svg";
import failedIcon from "../assets/icons/failed-alert.svg";

const filters = {
  UserTrade: "Buy/Sell",
  FiatTransaction: "Deposit/Withdrawal",
  Payment: "80eightPay",
  CryptoTransaction: "Send/Receive",
};

export function ArbitrageHistory({ setPage }) {
  const [filter, setFilter] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState([]);
  const [singleTransaction, setSingleTransaction] = useState(false);
  const [transactionsAll, setTransactionsAll] = useState(false);
  const [isFromAllTr, setIsFromAllTr] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const {
    data: transactionsComplete,
    isLoading,
    isError,
    errorTr,
  } = useUserTransactionHistory();

  const filteredTransactions = filter
    ? transactionsComplete?.filter((tx) => tx.transactionType === filter)
    : transactionsComplete;

  const itemsPerPage = 5;
  const totalPages = Math.ceil(filteredTransactions?.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTransactions?.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const formatTransactionDate = (createdAt) => {
    const now = new Date();
    const transactionDate = new Date(createdAt);
    const isToday = now.toDateString() === transactionDate.toDateString();
    const isYesterday =
      new Date(now.setDate(now.getDate() - 1)).toDateString() ===
      transactionDate.toDateString();
    if (isToday) {
      return `Today, ${transactionDate.getHours()}:${transactionDate
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;
    }
    if (isYesterday) {
      return `Yesterday, ${transactionDate.getHours()}:${transactionDate
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;
    }
    return transactionDate.toLocaleDateString("en-ZA", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleOpenTHModal = () => {
    setTransactionsAll(true);
  };

  const handleReOpenTHModal = () => {
    if (isFromAllTr) {
      setTransactionsAll(true);
    }
  };

  const handleSingleTransaction = (order) => {
    setIsFromAllTr(false);
    setSelectedTransaction(order);
    setSingleTransaction(true);
    setTransactionsAll(false);
  };

  return (
    <Card className="col-span-2 flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-[20px] text-text-secondary font-semibold">
          Transaction history
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 flex-1 flex flex-col">
        <div className="space-y-6 p-6">
          <div className="grid grid-cols-7 items-center gap-3">
            <div className="flex items-center gap-4 col-span-4">
              <div>
                <img src={arbIcon} alt="" />
              </div>
              <div>
                <div className="text-text-primary text-[20px] font-semibold">
                  Payout
                </div>
                <div className="text-gray-500 text-sm">Today, 11:30 AM</div>
              </div>
            </div>
            <div>
              <div className="flex justify-end text-[20px] text-text-primary font-semibold">
                R 590.89
              </div>
            </div>
            <div className="flex justify-end">
              <div className=" flex bg-green-50 px-2 py-1 rounded-full justify-center min-w-[80px]">
                <img src={approvedIcon} alt="" className="mr-2" />
                <p className="text-[10px] text-green-700 font-bold">SUCCESS</p>
              </div>
            </div>
            <div className="text-right">
              <Button
                onClick={() => setPage(1)}
                variant="outline"
                size="icon"
                className="rounded-full"
              >
                <ArrowRight className="w-5 h-5 text-gray-500" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-7 items-center gap-3">
            <div className="flex items-center gap-4 col-span-4">
              <div>
                <img src={arbIcon} alt="" />
              </div>
              <div>
                <div className="text-text-primary text-[20px] font-semibold">
                  Payout
                </div>
                <div className="text-gray-500 text-sm">Today, 11:30 AM</div>
              </div>
            </div>
            <div>
              <div className="flex justify-end text-[20px] text-text-primary font-semibold">
                R 590.89
              </div>
            </div>
            <div className="flex justify-end">
              <div className=" flex bg-red-50 px-2 py-1 rounded-full justify-center min-w-[80px]">
                <img src={failedIcon} alt="" className="mr-2" />
                <p className="text-[10px] text-red-600 font-bold">FAILED</p>
              </div>
            </div>
            <div className="text-right">
              <Button
                onClick={() => setPage(1)}
                variant="outline"
                size="icon"
                className="rounded-full"
              >
                <ArrowRight className="w-5 h-5 text-gray-500" />
              </Button>
            </div>
          </div>
        </div>
        {singleTransaction && (
          <TransactionHistorySingle
            selectedTransaction={selectedTransaction}
            openSingle={singleTransaction}
            setOpenSingle={setSingleTransaction}
            onClose={handleReOpenTHModal}
          />
        )}
        {transactionsAll && (
          <TransactionHistoryAll
            transactionsComplete={transactionsComplete}
            openAll={transactions}
            setOpenAll={setTransactionsAll}
            onOpenSingle={handleSingleTransaction}
            setIsFromAllTr={setIsFromAllTr}
          />
        )}
      </CardContent>
    </Card>
  );
}
